import type { TransactionItemDetail } from 'models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isSuperAdmin } from 'permissions';
import { Delete2LineIcon, Flag1LineIcon, type MenuProps, PencilLineIcon } from 'ui';
import { useUser } from 'features/auth';
import { useDeleteTestimonial } from './useDeleteTestimonial';
import { useReportTestimonial } from './useReportTestimonial';

type P = {
  onTestimonialEdit: (opened: true) => void;
  testimonial: TransactionItemDetail;
};

export const useActions = ({ onTestimonialEdit, testimonial }: P) => {
  const { railsUserData: viewer } = useUser();
  const { t } = useTranslation();
  const isViewerAuthor = testimonial.transaction?.user?.id === viewer.id;
  const { confirm: reportTestimonial, contextHolder: contextHolderReport } = useReportTestimonial({ testimonial });
  const { confirm: deleteTestimonial, contextHolder: contextHolderDelete } = useDeleteTestimonial({ testimonial });

  return useMemo(() => {
    const actions: MenuProps['items'] = [];

    const deleteAction = {
      danger: true,
      icon: <Delete2LineIcon />,
      key: 'delete',
      label: t('Delete'),
      onClick() {
        deleteTestimonial();
      },
    };

    if (isViewerAuthor) {
      actions.push(
        {
          icon: <PencilLineIcon />,
          key: 'edit',
          label: t('Edit'),
          onClick() {
            onTestimonialEdit(true);
          },
        },
        deleteAction,
      );
    } else {
      actions.push(
        {
          icon: <Flag1LineIcon />,
          key: 'report',
          label: t('Report'),
          onClick() {
            reportTestimonial();
          },
        },
        isSuperAdmin({ viewer }) ? deleteAction : null,
      );
    }

    const contextHolders = (
      <>
        {contextHolderDelete}
        {contextHolderReport}
      </>
    );

    return {
      actions,
      contextHolders,
    };
  }, [
    contextHolderDelete,
    contextHolderReport,
    deleteTestimonial,
    isViewerAuthor,
    onTestimonialEdit,
    reportTestimonial,
    t,
    viewer,
  ]);
};
